/**
 * Service Routes Composable
 *
 * Centralizes URL resolution for service pages using TARGET_KEYs.
 * Migrated from the Vue 2 servicePage mixin.
 *
 * Usage:
 *   const { resolveUrl } = useServiceRoutes()
 *   const href = resolveUrl('SECURITY_CCTV')
 */

/** Global service route mappings */
const SERVICE_ROUTES: Record<string, string> = {
  // =========================================================================
  // Internet & Data
  // =========================================================================
  INTERNET_DATA_HUB: '/internet-data/',
  INTERNET_DATA_NBN: '/internet-data/business-nbn/',
  ENTERPRISE_ETHERNET: '/internet-data/enterprise-ethernet',
  INTERNET_FIBRE: '/internet-data/fibre',
  IP_TRANSIT: '/internet-data/ip-transit',
  PRIVATE_IP_NETWORK: '/internet-data/private-ip-network',
  INTERNET_WIRELESS: '/internet-data/wireless-ethernet',

  // =========================================================================
  // Security Services
  // =========================================================================
  SECURITY_HUB: '/security-services/',
  SECURITY_CCTV: '/security-services/cctv',
  SECURITY_CCTV_SYDNEY: '/security-services/cctv-sydney',
  SECURITY_CCTV_BRISBANE: '/security-services/cctv-brisbane',
  SECURITY_CCTV_GOLD_COAST: '/security-services/cctv-gold-coast',
  SECURITY_RESIDENTIAL_CCTV: '/security-services/residential-cctv',
  SECURITY_RESIDENTIAL_CCTV_SYDNEY: '/security-services/residential-cctv-sydney',
  SECURITY_RESIDENTIAL_CCTV_BRISBANE: '/security-services/residential-cctv-brisbane',
  SECURITY_RESIDENTIAL_CCTV_GOLD_COAST: '/security-services/residential-cctv-gold-coast',
  SECURITY_ACCESS_CONTROL: '/security-services/access-control',
  SECURITY_ACCESS_CONTROL_BRISBANE: '/security-services/access-control-brisbane',
  SECURITY_ACCESS_CONTROL_GOLD_COAST: '/security-services/access-control-gold-coast',
  SECURITY_ACCESS_CONTROL_SYDNEY: '/security-services/access-control-sydney',
  SECURITY_ALARM: '/security-services/alarm-systems',
  SECURITY_ALARM_BRISBANE: '/security-services/alarm-systems-brisbane',
  SECURITY_ALARM_SYDNEY: '/security-services/alarm-systems-sydney',
  SECURITY_ALARM_GOLD_COAST: '/security-services/alarm-systems-gold-coast',
  SECURITY_RESIDENTIAL_ALARM: '/security-services/residential-alarm-systems',
  SECURITY_RESIDENTIAL_ALARM_SYDNEY: '/security-services/residential-alarm-systems-sydney',
  SECURITY_RESIDENTIAL_ALARM_BRISBANE: '/security-services/residential-alarm-systems-brisbane',
  SECURITY_RESIDENTIAL_ALARM_GOLD_COAST: '/security-services/residential-alarm-systems-gold-coast',
  SECURITY_INTERCOM_SOLUTIONS: '/security-services/intercom-solutions',
  SECURITY_INTERCOM_BRISBANE: '/security-services/intercom-solutions-brisbane',
  SECURITY_INTERCOM_SYDNEY: '/security-services/intercom-solutions-sydney',
  SECURITY_INTERCOM_GOLD_COAST: '/security-services/intercom-solutions-gold-coast',
  SECURITY_RESIDENTIAL_INTERCOM: '/security-services/residential-intercom-solutions',
  SECURITY_RESIDENTIAL_INTERCOM_SYDNEY: '/security-services/residential-intercom-solutions-sydney',
  SECURITY_RESIDENTIAL_INTERCOM_BRISBANE: '/security-services/residential-intercom-solutions-brisbane',
  SECURITY_RESIDENTIAL_INTERCOM_GOLD_COAST: '/security-services/residential-intercom-solutions-gold-coast',
  SECURITY_MONITORING_24_7: '/security-services/back-to-base-monitoring-24-7',
  SECURITY_ALARM_MONITORING: '/security-services/back-to-base-monitoring-24-7',
  SECURITY_ALARM_MONITORING_BRISBANE: '/security-services/alarm-monitoring-brisbane',
  SECURITY_ALARM_MONITORING_SYDNEY: '/security-services/alarm-monitoring-sydney',
  SECURITY_ALARM_MONITORING_GOLD_COAST: '/security-services/alarm-monitoring-gold-coast',
  SECURITY_RESIDENTIAL_ALARM_MONITORING: '/security-services/residential-alarm-monitoring',
  SECURITY_RESIDENTIAL_ALARM_MONITORING_SYDNEY: '/security-services/residential-alarm-monitoring-sydney',
  SECURITY_RESIDENTIAL_ALARM_MONITORING_BRISBANE: '/security-services/residential-alarm-monitoring-brisbane',
  SECURITY_RESIDENTIAL_ALARM_MONITORING_GOLD_COAST: '/security-services/residential-alarm-monitoring-gold-coast',
  SECURITY_LONE_WORKER: '/security-services/lone-worker-solutions',
  SECURITY_COMMERCIAL: '/security-services/commercial-security-solutions',
  SECURITY_SERVICE_AREAS: '/security-services/service-areas',

  // =========================================================================
  // Voice & Video
  // =========================================================================
  VOICE_HUB: '/voice-video/',
  VOICE_VOIP: '/voice-video/voip-phone/',
  VOICE_CLOUD_PABX: '/voice-video/cloud-pabx',
  MICROSOFT_TEAMS_VOICE: '/voice-video/microsoft-teams-voice',
  SIP_VOICE: '/voice-video/sip-voice',
  VIDEOCONFERENCING: '/voice-video/videoconferencing',
  CONTACT_CENTRE_FOR_TEAMS: '/voice-video/contact-centre-for-teams',
  VOICE_NUMBER_HOSTING: '/voice-video/number-hosting',
  VOICE_UNIFIED_COMMUNICATIONS: '/voice-video/unified-communications',

  // =========================================================================
  // Managed IT
  // =========================================================================
  IT_HUB: '/managed-it-services/',
  IT_SERVICE_DESK: '/managed-it-services/au-service-desk',
  IT_HARDWARE: '/managed-it-services/hardware-asset-management',
  IT_MONITORING: '/managed-it-services/remote-monitoring-management',
  IT_SECURITY: '/managed-it-services/online-and-system-security',
  IT_PROJECTS: '/managed-it-services/proactive-ict-projects',
  IT_STRATEGY: '/managed-it-services/strategies-roadmaps-and-insights',

  // =========================================================================
  // Project Services
  // =========================================================================
  PROJECT_HUB: '/project-services/',
  PROJECT_CABLING: '/project-services/structured-cabling-solutions',
  PROJECT_WIFI: '/project-services/wifi-solutions',
  PROJECT_ICT: '/project-services/integrated-ict-solutions',

  // =========================================================================
  // Radio Services
  // =========================================================================
  RADIO_HUB: '/radio-services/',
  RADIO_DESIGN: '/radio-services/radio-systems-design',
  RADIO_PTT: '/radio-services/push-to-talk',
  RADIO_DEPLOY: '/radio-services/deploy-maintain',
  RADIO_COMMISSIONING: '/radio-services/commissioning',

  // =========================================================================
  // Web Solutions
  // =========================================================================
  WEB_HUB: '/web-solutions/',
  WEB_DIGITAL_BRANDING: '/web-solutions/digital-branding',
  WEB_DOMAIN_HOSTING: '/web-solutions/domain-hosting',
  WEB_SSL_CERTIFICATES: '/web-solutions/ssl-certificates',
  WEB_VPS: '/web-solutions/virtual-private-server',
  WEB_DESIGN: '/web-solutions/website-design',
  WEB_EMAIL_HOSTING: '/web-solutions/email-hosting',

  // =========================================================================
  // Cloud Services
  // =========================================================================
  CLOUD_HUB: '/cloud-services/',
  CLOUD_BACKUP: '/cloud-services/cloud-backup',
  CLOUD_COLOCATION: '/cloud-services/data-centre-colocation',
  CLOUD_DR: '/cloud-services/cloud-disaster-recovery',
  CLOUD_KNOWLEDGE_BASE: '/cloud-services/cloud-knowledge-base',
  CLOUD_LMS: '/cloud-services/cloud-learning-management',
  CLOUD_PRIVATE: '/cloud-services/private-cloud',
  CLOUD_VIRTUALIZATION: '/cloud-services/virtualization',

  // =========================================================================
  // Industry Solutions
  // =========================================================================
  INDUSTRY_HUB: '/industry-security-solutions/',
  INDUSTRY_RETAIL: '/industry-security-solutions/retail-security',
  INDUSTRY_WAREHOUSE: '/industry-security-solutions/warehouse-security',
  INDUSTRY_CHILDCARE: '/industry-security-solutions/childcare-education',
  INDUSTRY_PROPERTY: '/industry-security-solutions/property-management',
  INDUSTRY_GYM: '/industry-security-solutions/gym-fitness',
  INDUSTRY_JOBSITE: '/industry-security-solutions/jobsite-surveillance-system',
  INDUSTRY_SCHOOL: '/industry-security-solutions/school-security-systems',
  INDUSTRY_SCHOOL_SYDNEY: '/industry-security-solutions/school-security-systems-sydney',
  INDUSTRY_SCHOOL_BRISBANE: '/industry-security-solutions/school-security-systems-brisbane',
  INDUSTRY_SCHOOL_GOLD_COAST: '/industry-security-solutions/school-security-systems-gold-coast',

  // =========================================================================
  // General Pages
  // =========================================================================
  CONTACT: '/contact',
  CONTACT_CONSULT: '/contact?type=consultation',
  ABOUT: '/about-us/',
  CASE_STUDIES: '/about-us/our-projects/',
  PARTNERS: '/about-us/our-partners',
  BLOG: '/blog/',
} as const

/** Type for valid TARGET_KEYs */
export type ServiceRouteKey = keyof typeof SERVICE_ROUTES

/**
 * Composable for resolving service page URLs from TARGET_KEYs
 */
export function useServiceRoutes() {
  /**
   * Resolve a TARGET_KEY to its URL path
   *
   * Supports two formats:
   * - Uppercase keys: "INTERNET_FIBRE" -> "/internet-data/fibre"
   * - Slug paths: "internet-data/fibre" -> "/internet-data/fibre"
   *
   * @param key - The TARGET_KEY or slug path to resolve
   * @param pageTargets - Optional page-level overrides (takes priority)
   * @returns The resolved URL path, or '#' if not found
   */
  const resolveUrl = (key: string, pageTargets?: Record<string, string>): string => {
    // Priority 1: page-level targets
    if (pageTargets?.[key]) {
      return pageTargets[key]
    }

    // Priority 2: uppercase TARGET_KEY in SERVICE_ROUTES
    if (SERVICE_ROUTES[key as ServiceRouteKey]) {
      return SERVICE_ROUTES[key as ServiceRouteKey]
    }

    // Priority 3: slug-path format (e.g., "internet-data/fibre" -> "/internet-data/fibre")
    // Only accept keys that look like valid slug paths (contain '/' and lowercase)
    if (key.includes('/') && key === key.toLowerCase() && !key.startsWith('/')) {
      return `/${key}`
    }

    // Fallback: warn and return '#'
    if (import.meta.dev) {
      console.warn(`[useServiceRoutes] TARGET_KEY not found: ${key}`)
    }

    return '#'
  }

  /**
   * Resolve a ContentLink to an href
   * Handles both direct href and targetKey resolution
   */
  const resolveLink = (
    link: { href?: string; targetKey?: string } | undefined,
    pageTargets?: Record<string, string>
  ): string => {
    if (!link) return '#'
    if (link.href) return link.href
    if (link.targetKey) return resolveUrl(link.targetKey, pageTargets)
    return '#'
  }

  /**
   * Check if a TARGET_KEY exists in the routes
   */
  const hasRoute = (key: string): boolean => {
    return key in SERVICE_ROUTES
  }

  /**
   * Get all routes for a specific category
   * Useful for generating navigation or sitemaps
   */
  const getRoutesByCategory = (prefix: string): Record<string, string> => {
    return Object.entries(SERVICE_ROUTES)
      .filter(([key]) => key.startsWith(prefix))
      .reduce(
        (acc, [key, value]) => {
          acc[key] = value
          return acc
        },
        {} as Record<string, string>
      )
  }

  return {
    resolveUrl,
    resolveLink,
    hasRoute,
    getRoutesByCategory,
    SERVICE_ROUTES,
  }
}
